import React, { useMemo } from 'react'
import Modal from '../../components/Modal/Modal'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { Button, SelectInput, TextInput } from '@demandlocal/components'
import { useNavigate, useParams } from 'react-router'
import { useGetDealershipsByAgencyId } from '../../api/agencies/useGetDealershipsByAgencyId'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { useGenerateReports } from '../../api/agencies/useGenerateReports'
import { useGetAgencyByAgencyId } from '../../api/agencies/useGetAgencyByAgencyId'
import { isEmpty } from 'lodash'

const GenerateReports = (): JSX.Element => {
  const { agency_id } = useParams()
  const formContext = useForm({
    mode: 'onSubmit',
  })

  const { mutateAsync: generateReports } = useGenerateReports()
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const { data: agencyDealerships, isLoading } = useGetDealershipsByAgencyId({
    pageSize: 100,
    filters: {
      agency_id: { value: agency_id },
    },
  })

  const agencyDealershipOptions = useMemo(
    () => agencyDealerships?.data?.map((d) => ({ label: d.dealership, value: d.id })),
    [agencyDealerships],
  )

  const { handleSubmit } = formContext

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Generating report...')
    generateReports({
      data: {
        agency_id: agency_id,
        dealership_ids: !isEmpty(values?.dealerships)
          ? values?.dealerships
          : agencyDealershipOptions,
        date: dayjs(values.date).startOf('month').format('YYYY-MM-DD'),
      },
    })
    await new Promise((resolve) => {
      setTimeout(resolve, 1500)
    })
    toast.success('Report are running. Check back in a few minutes.', {
      id: toastId,
    })
    navigate('..')
  }

  const navigate = useNavigate()
  return (
    <Modal key={'GenerateReportsForm'} isOpen={true}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-1 h-full flex-col overflow-y-scroll'}>
          <div>
            <div className={'flex flex-col flex-1'}>
              <div className={'flex flex-col gap-8 px-12 pt-12 '}>
                <div className={'font-bold text-[28px] text-primary-xiketic'}>
                  Generate reports for {agency?.name}
                </div>
                <div className={'flex flex-col gap-10'}>
                  <SelectInput
                    name={'dealerships'}
                    label={'Dealerships'}
                    options={agencyDealershipOptions ?? []}
                    isMulti={true}
                    isSearchable
                    isLoading={isLoading}
                    placeholder={'All dealerships'}
                  />
                  <div className="w-1/3">
                    <TextInput
                      name={'date'}
                      type={'month'}
                      label={'Booking Month'}
                      rules={{
                        required: true,
                        validate: {
                          mustBeAfterAugust2023: (date) => {
                            return dayjs(date).isAfter('2023-08-01')
                              ? true
                              : 'Date must be after July 2023'
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={
              'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
            }>
            <Button
              variant={'secondary'}
              onClick={(e) => {
                e.preventDefault()
                navigate('..')
              }}>
              Cancel
            </Button>
            <Button variant={'primary'}>Generate</Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default GenerateReports
